import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.text};
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const Header = styled.div`
  margin-bottom: 3rem;
  text-align: center;
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const LastUpdated = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.95rem;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const Content = styled.div`
  line-height: 1.8;
  
  h2 {
    color: ${props => props.theme.colors.primary};
    font-size: 1.5rem;
    margin-top: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 1rem;
  }
  
  ul {
    margin-left: 2rem;
    margin-bottom: 1rem;
    
    li {
      margin-bottom: 0.5rem;
    }
  }
  
  strong {
    color: ${props => props.theme.colors.text};
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    line-height: 1.6;
    
    h2 {
      font-size: 1.25rem;
      margin-top: 1.5rem;
    }
    
    ul {
      margin-left: 1.25rem;
    }
  }
`;

const ContactInfo = styled.div`
  background: ${props => props.theme.colors.backgroundSecondary};
  padding: 1.5rem;
  border-radius: 10px;
  margin-top: 2rem;
  
  p {
    margin: 0;
    font-size: 1rem;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    
    p {
      font-size: 0.9rem;
    }
  }
`;

const TermsAndConditions = () => {
  return (
    <Container>
      <Header>
        <Title>Terms and Conditions</Title>
        <LastUpdated>Last Updated: 17/10/2025</LastUpdated>
      </Header>

      <Content>
        <p>
          Welcome to Hustling ("the Platform", "we", "us", "our"). By accessing or using Hustling, 
          you agree to these Terms and Conditions. Please read them carefully before registering or 
          using our services.
        </p>

        <h2>1. About Hustling</h2>
        <p>
          Hustling is an online platform that connects entrepreneurs, startups, and skilled individuals 
          across South Africa. Users may post entrepreneurial opportunities ("Hustles") or one-time work 
          opportunities ("Gigs"), and other users may respond, connect, and collaborate.
        </p>

        <h2>2. Acceptance of Terms</h2>
        <p>By creating an account, accessing, or using the Platform, you:</p>
        <ul>
          <li>Confirm that you are 18 years or older.</li>
          <li>Agree to comply with these Terms and Conditions and our Privacy Policy.</li>
          <li>Consent to the collection, processing, and storage of your personal information in accordance with the Protection of Personal Information Act (POPIA).</li>
        </ul>
        <p>If you do not agree, you must not use the Platform.</p>

        <h2>3. User Accounts</h2>
        <ul>
          <li>You must create an account to access certain features of Hustling.</li>
          <li>You are responsible for maintaining the confidentiality of your account information.</li>
          <li>You are solely responsible for all activities that occur under your account.</li>
          <li>Hustling reserves the right to suspend or terminate any account that violates these Terms or is suspected of fraudulent or harmful activity.</li>
        </ul>

        <h2>4. User Content and Responsibilities</h2>
        <ul>
          <li>Users are responsible for the accuracy of all information posted, including business ideas, job descriptions, or personal details.</li>
          <li>You must not post misleading, false, offensive, or discriminatory content.</li>
          <li>You must comply with all applicable South African laws, including labour and tax regulations when entering into any agreements through the Platform.</li>
          <li>Hustling is not a party to any agreement made between users. Any engagement, payment, or employment is strictly between the users involved.</li>
        </ul>

        <h2>5. Payments and Fees</h2>
        <ul>
          <li>Hustling may charge service or connection fees for certain features (e.g., contacting a Hustle/Gig owner).</li>
          <li>All payments are processed through secure third-party payment providers.</li>
          <li>Hustling is not responsible for any payment disputes between users.</li>
        </ul>

        <h2>6. POPIA and Data Privacy</h2>
        <ul>
          <li>By using the Platform, you consent to Hustling collecting, processing, and storing your personal information as required to provide our services.</li>
          <li>Hustling will only use your data for legitimate business purposes, such as facilitating connections, improving services, and communicating updates.</li>
          <li>Your information will not be shared with third parties without your consent, except when required by law.</li>
          <li>You may request access to, correction of, or deletion of your data by contacting us at <a href="mailto:Knoopmarco@gmail.com">Knoopmarco@gmail.com</a>.</li>
          <li>Hustling takes reasonable measures to protect your personal information from unauthorized access or disclosure.</li>
        </ul>

        <h2>7. Intellectual Property</h2>
        <ul>
          <li>All website content, trademarks, logos, and design elements are owned by or licensed to Hustling.</li>
          <li>Users may not copy, reproduce, or distribute any part of the Platform without written permission.</li>
        </ul>

        <h2>8. Limitation of Liability</h2>
        <p><strong>8.1 Platform Purpose</strong></p>
        <ul>
          <li>Hustling provides the platform "as is" and makes no guarantees regarding the accuracy or reliability of user-posted content.</li>
          <li>Hustling is not an employer, agent, broker, or guarantor. We do not employ, recommend, or endorse any users.</li>
          <li>The Platform is solely a connection tool. Hustling is not a party to any agreements, arrangements, or transactions between users.</li>
        </ul>

        <p><strong>8.2 In-Person Meetings and Interactions</strong></p>
        <ul>
          <li><strong>All meetings, communications, and interactions between users are entirely at your own risk.</strong></li>
          <li>Hustling is not liable for any harm, injury, loss, or damage arising from in-person meetings or any form of interaction between users.</li>
          <li>Users are strongly advised to:
            <ul>
              <li>Meet in public, well-lit locations</li>
              <li>Inform trusted contacts about meeting details</li>
              <li>Verify the identity of other users before meeting</li>
              <li>Trust their instincts and exercise caution</li>
            </ul>
          </li>
        </ul>

        <p><strong>8.3 Fraud, Scams, and Misconduct</strong></p>
        <ul>
          <li>Hustling is not responsible for and cannot be held liable for:
            <ul>
              <li>Fraudulent listings, scams, or deceptive practices by users</li>
              <li>Non-payment, underpayment, or payment disputes</li>
              <li>Theft, misappropriation of funds, or intellectual property theft</li>
              <li>Harassment, discrimination, or any form of misconduct</li>
              <li>Failed business ventures, incomplete work, or breach of agreement</li>
              <li>Any damages (direct, indirect, or consequential) arising from user interactions</li>
            </ul>
          </li>
          <li>Hustling does not verify user identities, credentials, or the legitimacy of posted opportunities.</li>
          <li>Users must perform their own due diligence before engaging with any listing or user.</li>
        </ul>

        <p><strong>8.4 No Guarantees</strong></p>
        <ul>
          <li>Hustling makes no warranties or guarantees regarding:
            <ul>
              <li>The quality, safety, or legality of any Hustles or Gigs</li>
              <li>The truth or accuracy of user listings or profiles</li>
              <li>The ability of users to complete projects or fulfill obligations</li>
              <li>Any business outcomes, employment relationships, or financial results</li>
            </ul>
          </li>
        </ul>

        <p><strong>8.5 User Accountability</strong></p>
        <ul>
          <li>You acknowledge that all decisions to engage with other users, enter agreements, or meet in person are made entirely at your own discretion and risk.</li>
          <li>You agree that you will not hold Hustling liable for any negative outcomes, losses, or damages resulting from your use of the Platform or interactions with other users.</li>
        </ul>

        <p><strong>8.6 Third-Party Responsibility</strong></p>
        <ul>
          <li>All terms, conditions, payments, and arrangements made through the Platform are strictly between the users involved.</li>
          <li>Hustling is not responsible for enforcing agreements or resolving disputes between users.</li>
        </ul>

        <p><strong>8.7 Indemnification</strong></p>
        <ul>
          <li>You agree to indemnify, defend, and hold harmless Hustling, its owners, employees, and affiliates from any claims, damages, losses, or expenses (including legal fees) arising from:
            <ul>
              <li>Your use of the Platform</li>
              <li>Your interactions or agreements with other users</li>
              <li>Your violation of these Terms or applicable laws</li>
              <li>Any disputes between you and other users</li>
            </ul>
          </li>
        </ul>

        <h2>9. Termination</h2>
        <p>Hustling reserves the right to:</p>
        <ul>
          <li>Suspend or delete accounts that violate these Terms.</li>
          <li>Modify or discontinue the Platform at any time without prior notice.</li>
        </ul>

        <h2>10. Amendments</h2>
        <ul>
          <li>We may update these Terms from time to time. Continued use of the Platform after such updates constitutes your acceptance of the revised Terms.</li>
        </ul>

        <h2>11. Governing Law</h2>
        <ul>
          <li>These Terms are governed by and interpreted in accordance with the laws of the Republic of South Africa.</li>
          <li>Any disputes shall be subject to the exclusive jurisdiction of South African courts.</li>
        </ul>

        <h2>12. Contact Information</h2>
        <ContactInfo>
          <p>For questions, complaints, or data requests, contact us at:</p>
          <p><strong>📧 <a href="mailto:Knoopmarco@gmail.com">Knoopmarco@gmail.com</a></strong></p>
        </ContactInfo>
      </Content>
    </Container>
  );
};

export default TermsAndConditions;
